/* =======================================================================
   Knowledge Base — relevance engine.

   Ported verbatim from the mockup on 10 Sep 2026 (Azure item 416). Do not
   replace this casually: it took several rounds of tuning and every change
   was regression-tested against ~23 real-user phrasings. Examples that
   must keep working:
     how do i make a workorder · see who is off · add a new employee ·
     give my client a login · order parts for a job · dispatch board ·
     print paperwork for the customer

   Contractors do not type the words the docs use. "How do I make a
   workorder" has to reach an article titled "How do I create a work
   order?" — different verb, different spelling, six filler words. So a
   query is reduced to concepts and scored, rather than matched:

     1. strip filler words        how / do / i / a / the …
     2. stem                      creating, creates, created → creat
     3. expand through synonyms   make ≈ create ≈ add ≈ new ≈ raise
     4. score per field           title beats keywords beats body
     5. tolerate one typo         workoder → work order
     6. never dead-end            weak matches surface as "closest"

   Known tuning traps, all previously fixed:
     - `off` must NOT be a stopword — "time off" is a core concept
     - `create` and `created` must stem to the same root (strip trailing e)
     - highlighting must use known inflections, or `mak` matches "Makani"
     - only highlight the synonym the matched article actually used

   Pure functions over the static article list; nothing here touches the DOM.
   ======================================================================= */
import { written, strip } from './articles';

const STOP = new Set(('a an the and or but of for to in on at by as is are am was were be been being do does did done ' +
  'have has had how what when where which who whom whose why can could should would will shall may might must ' +
  'i me my we our you your it its this that these those there here about into from with without again ' +
  'more most some any all no not only own same so too very just please need want get got ' +
  'up down out over under s t don now').split(' '));   /* "off" is NOT filler here — "time off" */

/* Domain vocabulary. Each row is one concept; any word in the row
   reaches every article that uses any other word in the row. */
const SYN_GROUPS = [
  ['create','creating','make','making','add','adding','new','start','begin','raise','generate','enter','issue','open','initiate','build','set','setup','write'],
  ['delete','remove','void','cancel','erase','destroy','discard'],
  ['edit','change','update','modify','correct','amend','adjust','fix','alter','revise'],
  ['find','search','locate','lookup','filter','sort','browse','list','see','view','show','display'],
  ['assign','assigning','dispatch','allocate','schedule','send','attach'],
  ['technician','tech','techs','engineer','crew','worker','staff','person','people','guy','member'],
  ['workorder','wo','job','call','service'],
  ['po','purchaseorder','order'],
  ['photo','photos','picture','pictures','image','images','pic','pics','video','videos','snapshot'],
  ['note','notes','comment','comments','message','messages','chat','memo','remark'],
  ['status','statuses','state','states','stage','stages','progress','lifecycle','workflow'],
  ['hours','hour','time','labor','labour','timesheet','payroll','overtime','clock'],
  ['customer','client','account','site','location'],
  ['invoice','invoices','bill','billing','billed','receipt'],
  ['proposal','proposals','quote','quotes','estimate','bid'],
  ['upload','uploading','attach','import','add','put'],
  ['branch','branches','region','office','division'],
  ['nte','limit','cap','budget','maximum','max'],
  ['document','documents','doc','docs','file','files','paperwork','attachment'],
  ['ticket','printout','pdf','print','paperwork'],
  ['priority','urgent','critical','emergency','rush'],
  ['permission','permissions','role','roles','access','rights','privilege'],
  ['parts','part','material','materials','component','supply','supplies'],
  ['vendor','vendors','supplier','suppliers'],
  ['equipment','unit','machine','asset','freezer','cooler','compressor'],
  ['timeoff','vacation','pto','holiday','leave','absence','away','off','sick'],
  ['approve','approved','approval','reject','rejected','deny','decline','sign','authorise','authorize'],
  ['calendar','schedule','scheduling','dispatch','roster','board','week','month','day'],
  ['employee','employees','staff','team','member','members','colleague','hire','onboard','headcount'],
  ['role','roles','policy','policies','permission','permissions','access','rights','privilege']
];
/* Multi-word phrases that should collapse to a single concept. */
const SYN_PHRASES = [
  ['work order','workorder'], ['purchase order','po'], ['service call','workorder'],
  ['not to exceed','nte'], ['service ticket','ticket'], ['field ticket','ticket'],
  ['time sheet','timesheet'], ['get rid of','delete'],
  ['new employee','employee'], ['new hire','employee'], ['new starter','employee'],
  ['new tech','employee'], ['new technician','employee'], ['team member','employee'],
  ['time off','timeoff'], ['days off','timeoff'], ['day off','timeoff'],
  ['annual leave','timeoff'], ['sick day','timeoff'], ['who is off','timeoff'],
  ['set up','create'], ['how to','']
];

function norm(s){ return (s || '').toLowerCase().replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim(); }

/* Deliberately crude suffix stripping — enough to join create/creating/creates. */
function stem(w){
  if (w.length < 4) return w;
  w = w.replace(/(ings|ing|ies|ied|es|ed|s|ly|er|ers)$/,'');
  w = w.replace(/e$/,'');            /* create → creat, matching created/creating */
  return w.replace(/(.)\1$/,'$1');
}

const GROUP_OF = new Map();
SYN_GROUPS.forEach((g, i) => g.forEach(w => {
  const k = stem(w);
  if (!GROUP_OF.has(k)) GROUP_OF.set(k, []);
  GROUP_OF.get(k).push('g' + i);
}));

/* A string becomes a set of concepts: its own stems plus any synonym groups. */
function concepts(text){
  let t = ' ' + norm(text) + ' ';
  const out = new Set();
  SYN_PHRASES.forEach(([phrase, canon]) => {
    if (t.includes(' ' + phrase + ' ')){
      t = t.split(' ' + phrase + ' ').join(' ' + canon + ' ');
    }
  });
  norm(t).split(' ').forEach(w => {
    if (!w || STOP.has(w)) return;
    const s = stem(w);
    if (!s) return;
    out.add(s);
    (GROUP_OF.get(s) || []).forEach(g => out.add(g));
  });
  return out;
}

/* One-edit-distance check, for typos like "workoder". */
function near(a, b){
  if (Math.abs(a.length - b.length) > 1) return false;
  if (a === b) return true;
  if (a.length < 4 || b.length < 4) return false;
  let i = 0, j = 0, diff = 0;
  while (i < a.length && j < b.length){
    if (a[i] === b[j]){ i++; j++; continue; }
    if (++diff > 1) return false;
    if (a.length > b.length) i++;
    else if (a.length < b.length) j++;
    else { i++; j++; }
  }
  return diff + (a.length - i) + (b.length - j) <= 1;
}

/* Ordered concepts, used for adjacency. "add a work order" and "add
   photos … to a work order" share the same words; only word order
   separates them. */
function conceptSeq(text){
  let t = ' ' + norm(text) + ' ';
  SYN_PHRASES.forEach(([p, c]) => { if (t.includes(' ' + p + ' ')) t = t.split(' ' + p + ' ').join(' ' + c + ' '); });
  return norm(t).split(' ').filter(w => w && !STOP.has(w)).map(w => {
    const s = stem(w);
    return [s].concat(GROUP_OF.get(s) || []);
  });
}
function bigrams(text){
  const seq = conceptSeq(text), out = new Set();
  for (let i = 0; i < seq.length - 1; i++)
    seq[i].forEach(x => seq[i + 1].forEach(y => out.add(x + '|' + y)));
  return out;
}

/* Field weights: a hit in the title means far more than one in the body. */
const FIELD_W = {title: 14, kw: 9, answer: 5, body: 2};
let INDEX = null;
function buildIndex(){
  INDEX = written.map(a => {
    const f = {
      title: concepts(a.title),
      kw: concepts((a.keywords || []).join(' ')),
      answer: concepts(strip(a.answer)),
      body: concepts(strip(a.body))
    };
    const all = new Set();
    Object.values(f).forEach(s => s.forEach(c => all.add(c)));
    return {
      a, f, all,
      titleBi: bigrams(a.title),
      bodyBi: bigrams((a.keywords || []).join(' ') + '. ' + strip(a.answer) + ' ' + strip(a.body)),
      titleSize: f.title.size,
      norm: norm(a.title + ' ' + (a.keywords || []).join(' ') + ' ' + strip(a.answer) + ' ' + strip(a.body))
    };
  });
}

function matches(query){
  if (!INDEX) buildIndex();
  const qc = [...concepts(query)];
  const nq = norm(query);
  const qBi = bigrams(query);
  if (!qc.length) return [];

  const res = INDEX.map(entry => {
    let score = 0, hit = 0;
    qc.forEach(c => {
      let best = 0;
      for (const field of ['title','kw','answer','body']){
        if (entry.f[field].has(c)){ best = FIELD_W[field]; break; }
      }
      if (!best){                              /* nothing exact — allow one typo */
        for (const cand of entry.all){
          if (near(c, cand)){ best = 2; break; }
        }
      }
      if (best){ score += best; hit++; }
    });
    if (!hit) return null;

    const coverage = hit / qc.length;
    /* Reward covering more of what was asked, not just one strong word. */
    score = score * (0.45 + 0.55 * coverage);

    /* Adjacency. "add work order" next to each other in a title is a far
       stronger signal than the same words scattered through one. */
    qBi.forEach(g => {
      if (entry.titleBi.has(g)) score += 20;
      else if (entry.bodyBi.has(g)) score += 5;
    });

    /* Title precision — how much of the title the question accounts for.
       A short title the query almost fully covers beats a long one where
       most of the words were never asked about. */
    let tHit = 0;
    entry.f.title.forEach(c => { if (qc.indexOf(c) > -1) tHit++; });
    if (entry.titleSize) score += 22 * (tHit / entry.titleSize);

    if (entry.norm.includes(nq)) score += 20;                  /* whole phrase present */
    if (norm(entry.a.title).includes(nq)) score += 15;
    return {a: entry.a, score, coverage};
  }).filter(Boolean).sort((x, y) => y.score - x.score);

  return res;
}

/* The literal word stems to paint yellow — query words plus whatever
   synonym the article actually used. */
function searchTerms(query, constrainText){
  const qc = concepts(query);
  const words = new Set();
  norm(query).split(' ').forEach(w => { if (w && !STOP.has(w) && w.length > 2) words.add(stem(w)); });
  /* Only paint the synonym the matched article actually used. Lighting up
     every word in the group turns the page yellow and explains nothing. */
  const allow = constrainText ? concepts(constrainText) : null;
  SYN_GROUPS.forEach((g, i) => {
    if (!qc.has('g' + i)) return;
    g.forEach(w => {
      if (w.length <= 2) return;
      const s = stem(w);
      if (!allow || allow.has(s)) words.add(s);
    });
  });
  return [...words].filter(w => w.length > 2);
}

/* Stem plus a known inflection, so "mak" reaches make/makes/making but
   never Makani. */
const SUFFIX = '(?:e|es|s|ed|ing|ings|er|ers|ies|ied)?';
function termRe(stems, flags){
  const esc = s => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp('\\b(?:' + stems.map(esc).join('|') + ')' + SUFFIX + '\\b', flags);
}


export { matches, searchTerms, concepts, termRe, stem, norm };
